import { useEffect, useRef, useState, type ReactNode } from "react";
import type { Pagination } from "../../api/admin";
import { Modal } from "../Modal";

export function useDebouncedValue<T>(value: T, delay = 320) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timer);
  }, [delay, value]);
  return debounced;
}

export function AdminSkeleton({ rows = 5 }: { rows?: number }) {
  return <div className="admin-skeleton" aria-label="正在加载" role="status">{Array.from({ length: rows }, (_, index) => <span key={index} />)}</div>;
}

export function AdminEmpty({ title, description }: { title: string; description: string }) {
  return <div className="admin-empty"><b>{title}</b><p>{description}</p></div>;
}

export function AdminError({ message, retry }: { message: string; retry: () => void }) {
  return <div className="admin-inline-error" role="alert"><span>{message}</span><button onClick={retry}>重试</button></div>;
}

export function AdminPager({ pagination, onPage }: { pagination: Pagination; onPage: (page: number) => void }) {
  return (
    <div className="admin-pager">
      <span>第 {pagination.page} / {pagination.pages} 页，共 {pagination.total} 条</span>
      <div>
        <button disabled={!pagination.has_previous} onClick={() => onPage(pagination.page - 1)}>上一页</button>
        <button disabled={!pagination.has_next} onClick={() => onPage(pagination.page + 1)}>下一页</button>
      </div>
    </div>
  );
}

export interface ConfirmState {
  title: string;
  description: string;
  confirmLabel: string;
  tone?: "danger" | "default";
  requireReason?: boolean;
  onConfirm: (reason: string) => Promise<void>;
}

export function AdminConfirmDialog({ state, close }: { state: ConfirmState | null; close: () => void }) {
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    setReason("");
    setError("");
    setSubmitting(false);
  }, [state]);
  if (!state) return null;
  const currentState = state;
  const reasonRequired = currentState.requireReason !== false;
  async function submit() {
    if (reasonRequired && reason.trim().length < 3) {
      setError("请输入至少 3 个字符的操作原因");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await currentState.onConfirm(reason.trim());
      close();
    } catch (reasonError) {
      setError(reasonError instanceof Error ? reasonError.message : "操作失败");
    } finally {
      setSubmitting(false);
    }
  }
  return (
    <Modal open title={currentState.title} description={currentState.description} eyebrow="操作确认" busy={submitting} onClose={close}>
      <section className="admin-dialog admin-dialog--embedded">
        <span className={`admin-dialog__signal ${currentState.tone === "danger" ? "is-danger" : ""}`} />
        {reasonRequired ? <label>操作原因<textarea autoFocus value={reason} maxLength={255} onChange={(event) => setReason(event.target.value)} placeholder="原因会随操作记录保留" /></label> : null}
        {error ? <div className="admin-dialog__error" role="alert">{error}</div> : null}
        <footer>
          <button className="button button--quiet" disabled={submitting} onClick={close}>取消</button>
          <button className={`button ${currentState.tone === "danger" ? "admin-button--danger" : "button--primary"}`} disabled={submitting} onClick={() => void submit()}>
            {submitting ? <span className="button-spinner" /> : null}{currentState.confirmLabel}
          </button>
        </footer>
      </section>
    </Modal>
  );
}

export function AdminNotice({ notice, clear }: { notice: { tone: "success" | "error"; message: string } | null; clear: () => void }) {
  const clearRef = useRef(clear);
  clearRef.current = clear;
  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => clearRef.current(), 4200);
    return () => window.clearTimeout(timer);
  }, [notice]);
  return notice ? <button className={`admin-toast is-${notice.tone}`} onClick={clear} role="status">{notice.message}</button> : null;
}

export function AdminToolbar({ children }: { children: ReactNode }) {
  return <div className="admin-toolbar">{children}</div>;
}
